import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ProzessAnsicht, { ProzessSchritt } from './ProzessAnsicht';
import './ProzessAnsicht.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface ProzessAusloeser {
  typ: string;
  ereignis: string;
  bedingung_felder: string[];
}

interface ProzessEintrag {
  key: string;
  name: string;
  beschreibung: string;
  aktiv: boolean;
  grund: string | null;
  zuweisbar: boolean;
  module: string[];
  branchen: string[];
  ausloeser: ProzessAusloeser[];
  schritte: ProzessSchritt[];
  statuskette: string[];
  zugewiesen_an: Array<string | { name?: string; anzeigename?: string; benutzername?: string }>;
}

interface Props {
  authToken: string;
}

function zugewiesenName(z: ProzessEintrag['zugewiesen_an'][number]): string {
  if (typeof z === 'string') return z;
  return z.name || z.anzeigename || z.benutzername || '';
}

function schrittKurzform(schritte: ProzessSchritt[]): string {
  if (schritte.length === 0) return '–';
  const kurz = (t: string) => (t.length > 22 ? t.slice(0, 21) + '…' : t);
  const vorschau = schritte.slice(0, 3).map(s => kurz(s.was)).join(' → ');
  return `${schritte.length} Schritte: ${vorschau}${schritte.length > 3 ? ' …' : ''}`;
}

const ProzessKatalog: React.FC<Props> = ({ authToken }) => {
  const [prozesse, setProzesse] = useState<ProzessEintrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState('');
  const [offen, setOffen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFehler('');
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await axios.get(`${API_URL}/api/prozesse`, { headers });
      setProzesse(res.data);
    } catch {
      setFehler('Prozesse konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Prozesse</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--ink-2)', fontSize: 13 }}>
          Katalog der Abläufe, die digitale Mitarbeiter übernehmen können. Zeile anklicken für die Prozessansicht.
        </p>
      </div>

      {loading ? (
        <div style={{ padding: 16 }}>Lade Prozesse…</div>
      ) : fehler ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          fontSize: 13, color: 'var(--red)',
        }}>
          <span>{fehler}</span>
          <button className="k-btn k-btn--danger k-btn--sm" onClick={load} style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            Erneut versuchen
          </button>
        </div>
      ) : (
        <table className="k-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Schritte</th>
              <th>Status</th>
              <th>Zuweisbar</th>
              <th>Zugewiesen an</th>
            </tr>
          </thead>
          <tbody>
            {prozesse.map(p => {
              const istOffen = offen === p.key;
              const namen = p.zugewiesen_an.map(zugewiesenName).filter(Boolean);
              return (
                <React.Fragment key={p.key}>
                  <tr
                    className={`pz-katalog-zeile${istOffen ? ' is-offen' : ''}`}
                    onClick={() => setOffen(istOffen ? null : p.key)}
                  >
                    <td style={{ fontWeight: 600 }}>
                      <button
                        type="button"
                        className="pz-zeilen-knopf"
                        aria-expanded={istOffen}
                        onClick={e => {
                          e.stopPropagation();
                          setOffen(istOffen ? null : p.key);
                        }}
                      >
                        {p.name}
                      </button>
                      {p.beschreibung && <span className="pz-beschreibung">{p.beschreibung}</span>}
                    </td>
                    <td>{schrittKurzform(p.schritte)}</td>
                    <td>
                      <span className={`pz-chip ${p.aktiv ? 'pz-chip--aktiv' : 'pz-chip--inaktiv'}`}>
                        {p.aktiv ? 'aktiv' : 'inaktiv'}
                      </span>
                      {!p.aktiv && p.grund && <span className="pz-grund">{p.grund}</span>}
                    </td>
                    <td>{p.zuweisbar ? 'ja' : 'nein'}</td>
                    <td>{namen.length > 0 ? namen.join(', ') : '–'}</td>
                  </tr>
                  {istOffen && (
                    <tr className="pz-detail">
                      <td colSpan={5}>
                        <ProzessAnsicht prozess={p} authToken={authToken} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {prozesse.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-2)' }}>
                  Keine Prozesse vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProzessKatalog;
